import { Link } from "react-router-dom";
import { ArrowRight, Award, Shield, CheckCircle, MapPin, Phone, Users, Brain, HeartHandshake, Utensils, Pill, HandHelping, ExternalLink } from "lucide-react";
import { WarmLayout } from "../layouts/WarmLayout";
import { WarmHero } from "../components/WarmHero";
import { WarmProviderPreview } from "../components/WarmProviderPreview";
import { WarmPhotoCarousel } from "../components/WarmPhotoCarousel";
import { WarmTestimonial } from "../components/WarmTestimonial";
import { DecisionTools } from "../components/DecisionTools";
import { WarmActivities } from "../components/WarmActivities";
import { WarmCompare } from "../components/WarmCompare";
import { WarmPricing } from "../components/WarmPricing";
import { WarmParallax } from "../components/WarmParallax";
import { FAQSection } from "../components/FAQSection";
import { useLocation } from "@/contexts/LocationContext";
import { getOtherLocation } from "@/lib/locations";
import { testimonials, specialtyBadges } from "@/lib/content";

// Editorial step marker — an oversized Fraunces numeral that hangs at the left
// margin with the label beside it. Off-axis on purpose: the journey reads as
// chaptered, and the page stops feeling rigidly centered.
function StepIndicator({ step, label }: { step: number; label: string }) {
  return (
    <div className="reveal container mx-auto mb-2 px-4">
      <div className="flex items-end gap-4">
        <span
          aria-hidden="true"
          className="numeral text-6xl leading-[0.8] text-accent-strong/75 md:text-7xl"
        >
          {String(step).padStart(2, "0")}
        </span>
        <span className="mb-1.5 flex items-center gap-3">
          <span className="h-px w-8 bg-border" aria-hidden="true" />
          <span className="eyebrow text-accent-strong">{label}</span>
        </span>
      </div>
    </div>
  );
}

// Editorial section header — kicker + display heading with an optional Fraunces
// italic accent word, and an optional supporting line. Centered, reveal-aware.
function SectionHeader({
  kicker,
  children,
  sub,
  className = "",
}: {
  kicker: string;
  children: React.ReactNode;
  sub?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`reveal mx-auto max-w-2xl text-center ${className}`}>
      <span className="eyebrow text-accent-strong">{kicker}</span>
      <h2 className="mt-4 display-md font-heading font-semibold tracking-[-0.025em] text-foreground">
        {children}
      </h2>
      {sub && <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{sub}</p>}
    </div>
  );
}

export default function WarmHomePage() {
  const { location, locationId, isSeattle } = useLocation();
  const basePath = isSeattle ? "/seattle" : "";
  const otherLocation = getOtherLocation(locationId);

  return (
    <WarmLayout>
      {/* Step 1 is the hero itself — it carries the brand + first decision. */}
      <WarmHero />

      {/* Band: clean white — "Life at Our Home" */}
      <div className="band-surface">
        <WarmPhotoCarousel />
      </div>

      {/* Band: warm cream — Decision Tools */}
      <div className="band-cream grain">
        <div className="pt-8">
          <StepIndicator step={2} label="Is This Right for You?" />
        </div>
        <DecisionTools />
      </div>

      {/* Band: clean white — Provider story */}
      <div className="band-surface">
        <div className="pt-8">
          <StepIndicator step={3} label="Let's Connect" />
        </div>
        <WarmProviderPreview />
      </div>

      {/* 4. Services Preview — warm cream band.
           Asymmetric split header (title left, supporting copy + CTA offset right)
           and a diagonally staggered card grid, so the section reads off-axis. */}
      <section className="band-cream grain py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="reveal grid items-end gap-6 md:grid-cols-12">
            <div className="md:col-span-7">
              <span className="eyebrow text-accent-strong">How We Care</span>
              <h2 className="mt-4 display-lg font-heading font-semibold tracking-[-0.03em] text-foreground">
                What our care looks like,{" "}
                <span className="font-display-italic font-normal text-accent-strong">day to day</span>
              </h2>
            </div>
            <div className="md:col-span-5 md:pb-1.5">
              <p className="text-lg leading-relaxed text-muted-foreground">
                Consistent, attentive support guided by each resident&rsquo;s needs — the
                care we&rsquo;d want for our own family.
              </p>
              <Link
                to={`${basePath}/services`}
                className="group mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary transition-colors hover:text-accent-strong"
              >
                Explore all 14 services
                <ArrowRight className="h-4 w-4 transition-transform duration-200 ease-out-expo group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>

          {/* No reveal-group here: the diagonal translate-y stagger below is a static
              transform, and a reveal animation would reset transform to 0 and flatten it. */}
          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: Shield, title: "24/7 Supervision and Care", description: "Continuous staff presence around the clock ensuring help is always available. A safe, secure environment where families can feel confident." },
              { icon: Brain, title: "Dementia & Alzheimer's Support", description: "Patient, consistent support through structured routines and familiar surroundings that help reduce confusion and maintain dignity." },
              { icon: HeartHandshake, title: "Mental Health & Wellness", description: "Daily companionship, meaningful conversation, and respectful engagement that helps residents feel valued and connected." },
              { icon: Utensils, title: "Nutritious Home-Cooked Meals", description: "Fresh meals prepared daily with attention to nutrition, dietary restrictions, and cultural preferences in a family-style setting." },
              { icon: Pill, title: "Medication Management", description: "Careful assistance with medication reminders, coordinated with healthcare providers and pharmacies to support each care plan." },
              { icon: HandHelping, title: "Compassionate Hospice Support", description: "A warm, familiar environment with attentive daily support, working closely with hospice providers for comfort and peace." },
            ].map((service, i) => (
              <div
                key={service.title}
                // Diagonal stagger: each column sits a step lower than the last, so
                // the grid steps down instead of locking to a flat baseline.
                className={`group rounded-2xl border border-border bg-card p-6 transition-[box-shadow,border-color,transform] duration-200 ease-out-expo hover:border-accent/40 hover:shadow-lg ${
                  i % 3 === 1 ? "lg:translate-y-6" : i % 3 === 2 ? "lg:translate-y-12" : ""
                }`}
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-accent-strong transition-colors group-hover:bg-accent/15">
                  <service.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 text-lg font-semibold tracking-[-0.01em] text-foreground">{service.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* 4b. Compare your options — clean white band */}
      <div className="band-surface">
        <WarmCompare />
      </div>

      {/* 5. A Day in the Life — warm cream band */}
      <div className="band-cream grain">
        <WarmActivities />
      </div>

      {/* 5b. Cost & Payment — clean white band */}
      <div className="band-surface">
        <WarmPricing />
      </div>

      {/* 6. License Section - Only show for Lynnwood (Seattle license pending) */}
      {!isSeattle && (
        <section id="license-section" className="band-cream grain py-16">
          <div className="container mx-auto px-4">
            <div className="reveal mx-auto mb-12 max-w-2xl text-center">
              <span className="eyebrow text-accent-strong">Our Credentials</span>
              <h2 className="mt-4 display-md font-heading font-semibold tracking-[-0.025em] text-foreground">
                State{" "}
                <span className="font-display-italic font-normal text-accent-strong">licensed</span>{" "}
                facility
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-muted-foreground">
                Licensed by the Washington State Department of Social and Health Services (DSHS) —
                and verifiable on the public state registry.
              </p>
            </div>
            
            <div className="mx-auto max-w-3xl">
              <div className="rounded-3xl border border-border/50 bg-card p-6 shadow-lg overflow-hidden">
                <img 
                  src={location.licenseImage} 
                  alt={`Washington State DSHS Adult Family Home License - License #${location.licenseNumber}`} 
                  className="w-full h-auto rounded-xl"
                />
              </div>
              
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                <div className="flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary">
                  <Shield className="h-4 w-4" />
                  License #{location.licenseNumber}
                </div>
                <div className="flex items-center gap-2 rounded-full bg-accent/10 px-4 py-2 text-sm font-medium text-accent-strong">
                  <CheckCircle className="h-4 w-4" />
                  DSHS Washington State
                </div>
              </div>

              {/* Verifiable, not just displayed — invite families to confirm us
                  (and any home) with the state. Transparency reads as confidence. */}
              <div className="mt-6 text-center">
                <a
                  href="https://fortress.wa.gov/dshs/adsaapps/lookup/AFHPubLookup.aspx"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-primary/30 px-5 py-2.5 text-sm font-medium text-primary underline-offset-4 transition-colors hover:bg-primary/5 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  <ExternalLink className="h-4 w-4" />
                  Verify this license on the WA State registry
                </a>
                <p className="mx-auto mt-3 max-w-md text-xs leading-relaxed text-muted-foreground">
                  We encourage you to check our record — or any home's — with Washington
                  DSHS. Questions or concerns about any adult family home can go to the state's
                  Complaint Resolution Unit at{" "}
                  <a href="tel:18005626078" className="font-medium text-foreground hover:text-accent-strong">
                    1-800-562-6078
                  </a>
                  .
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 6b. Parallax "come see it" band — real photo, Lynnwood only (honest media) */}
      {!isSeattle && <WarmParallax />}

      {/* 7. Testimonials Preview — clean white band */}
      <section className="band-surface py-20">
        <div className="container mx-auto px-4">
          <SectionHeader kicker="Family Stories">
            What families say{" "}
            <span className="font-display-italic font-normal text-accent-strong">about us</span>
          </SectionHeader>

          <div className="reveal-group mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <WarmTestimonial key={index} {...testimonial} />
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <Link
              to={`${basePath}/testimonials`}
              className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-accent-strong transition-colors"
            >
              Read More Stories
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* 8. Trusted by Families — DEEP NAVY anchor band (the one high-contrast section) */}
      <section className="band-deep grain py-20">
        <div className="container mx-auto px-4">
          <div className="reveal mx-auto mb-14 max-w-2xl text-center">
            <span className="eyebrow text-[hsl(var(--accent-on-dark))]">Serving Greater Seattle</span>
            <h2 className="mt-4 display-md font-heading font-semibold tracking-[-0.025em] text-primary-foreground">
              Trusted by families{" "}
              <span className="font-display-italic font-normal text-[hsl(var(--accent-on-dark))]">across the region</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-primary-foreground/70">
              Two homes, five years of care, and a reputation built one family at a time.
            </p>
          </div>
          
          {/* Location Cards */}
          <div className="grid gap-6 md:grid-cols-2 max-w-4xl mx-auto">
            {/* Current Location Card */}
            <div className={`rounded-3xl border-2 ${!isSeattle ? "border-accent/40 bg-card" : "border-border/50 bg-card"} p-6 shadow-lg`}>
              <div className="flex items-center justify-between mb-4">
                <span className={`inline-flex items-center gap-1.5 rounded-full ${!isSeattle ? "bg-accent/15 text-accent-strong" : "bg-primary/10 text-primary"} px-3 py-1 text-xs font-semibold`}>
                  <MapPin className="h-3 w-3" />
                  Lynnwood
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-green-500/15 px-2.5 py-1 text-xs font-medium text-green-600">
                  <Users className="h-3 w-3" />
                  Now Accepting
                </span>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                4516 145th Pl SW<br />
                Lynnwood, WA 98087
              </p>
              <a href="tel:2065570011" className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-accent-strong transition-colors mb-4">
                <Phone className="h-4 w-4 text-accent-strong" />
                206-557-0011
              </a>
              <Link
                to="/contact#tour-form"
                className={`w-full inline-flex items-center justify-center gap-2 rounded-full ${!isSeattle ? "bg-accent text-accent-foreground hover:bg-accent/90" : "bg-primary/10 text-primary hover:bg-primary/20"} px-4 py-2.5 text-sm font-semibold transition-colors`}
              >
                {!isSeattle ? "Schedule a Tour" : "View Lynnwood Home"}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            
            {/* Seattle Location Card */}
            <div className={`rounded-3xl border-2 ${isSeattle ? "border-accent/40 bg-card" : "border-border/50 bg-card"} p-6 shadow-lg`}>
              <div className="flex items-center justify-between mb-4">
                <span className={`inline-flex items-center gap-1.5 rounded-full ${isSeattle ? "bg-accent/15 text-accent-strong" : "bg-primary/10 text-primary"} px-3 py-1 text-xs font-semibold`}>
                  <MapPin className="h-3 w-3" />
                  Seattle
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-accent/15 px-2.5 py-1 text-xs font-medium text-accent-strong">
                  <Users className="h-3 w-3" />
                  Waitlist Open
                </span>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                {otherLocation.id === "seattle" ? otherLocation.address : location.address}<br />
                {otherLocation.id === "seattle" ? otherLocation.cityState : location.cityState}
              </p>
              <a href="tel:2065570011" className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-accent-strong transition-colors mb-4">
                <Phone className="h-4 w-4 text-accent-strong" />
                206-557-0011
              </a>
              <Link
                to={isSeattle ? "/seattle/contact#tour-form" : "/seattle/"}
                className={`w-full inline-flex items-center justify-center gap-2 rounded-full ${isSeattle ? "bg-accent text-accent-foreground hover:bg-accent/90" : "bg-primary/10 text-primary hover:bg-primary/20"} px-4 py-2.5 text-sm font-semibold transition-colors`}
              >
                {isSeattle ? "Join Our Waitlist" : "View Seattle Home"}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
          
          {/* Specialty Badges */}
          <div className="mt-12 flex flex-wrap justify-center gap-4">
            {specialtyBadges.map((badge) => (
              <div
                key={badge}
                className="flex items-center gap-2 rounded-xl bg-card px-4 py-3 shadow-sm"
              >
                <CheckCircle className="h-4 w-4 text-accent-strong" />
                <span className="text-sm font-medium text-foreground">{badge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. FAQ Section */}
      <FAQSection />
    </WarmLayout>
  );
}
